import classNames from 'classnames';
import { format } from 'date-fns';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import { IHome } from '@/modules/home/types';
import { Typography } from '@/shared/components/atoms';
import { CircularBgImage } from '@/shared/components/atoms';
import routes from '@/shared/constant/routes';

import Archive from '~/images/shared/Archive.svg';
import Clipboard from '~/images/shared/clipboard-tick.svg';
import Timer from '~/images/shared/ic-time2.svg';
import Scissors from '~/images/shared/Scissors.svg';

const TooltipContent: React.FC<{ field: IHome.IField }> = ({ field }) => {
  const { t } = useTranslation();

  const operationStatus = [
    {
      icon: <Scissors width='24' height='24' />,
      title: t('In-Progress Operations'),
      bg: 'bg-neutralVariant-96',
    },
    {
      icon: <Timer width='24' height='24' />,
      title: t('To-do Operations'),
      bg: 'bg-outline-default',
    },
    {
      icon: <Clipboard width='24' height='24' />,
      title: t('Done Operations'),
      bg: 'bg-secondary-90',
    },
    {
      icon: <Archive width='24' height='24' />,
      title: t('archived-field'),
      bg: '',
    },
  ];

  const content = [field.inProgressTasks, field.toDoTasks, field.doneTasks].map(
    (tasks, idx) => {
      const mergedTasks = [
        ...tasks.per.map((task) => ({ ...task, type: 'per' })),
        ...tasks.tech.map((task) => ({ ...task, type: 'tech' })),
      ];

      return mergedTasks.length ? (
        <div key={`tasks${idx}`} className='flex gap-8'>
          <span>{operationStatus[idx].icon}</span>
          <div>
            <Typography className='mb-4'>
              {operationStatus[idx].title}
            </Typography>
            {/* TODO: figure otu what to do with icon*/}
            <div className='flex flex-wrap gap-8'>
              {mergedTasks.map((task) => (
                <Link
                  className={classNames(
                    'flex cursor-pointer items-center gap-4 rounded-xl py-8 px-12',
                    operationStatus[idx].bg
                  )}
                  key={task.id}
                  href={`${
                    task.type === 'per'
                      ? routes.periodicJobs
                      : routes.workingDay
                  }/${task.id}`}
                >
                  {task.icon && (
                    <CircularBgImage size='sm' imageSrc={task.icon} />
                  )}
                  <Typography
                    variant='caption'
                    weight='semiBold'
                  >{`${task.name} #${task.id}`}</Typography>
                </Link>
              ))}
            </div>
          </div>
        </div>
      ) : null;
    }
  );

  return (
    <div className='flex max-w-md flex-col gap-16 p-16'>
      <div className='mb-4 flex items-center gap-8'>
        {field?.image && (
          <CircularBgImage
            size='sm'
            imageSrc={field.image}
            imageClassName='scale-125'
            bgColor='bg-outline-default'
          />
        )}
        <Typography weight='semiBold' className='whitespace-nowrap'>
          {field.name}
          {field.cropName && ` - ${field.cropName}`}
        </Typography>
      </div>
      {content.filter((item) => item).length ? content : ''}

      {field.fieldStatus === 'INACTIVE' && field.updatedAt && (
        <div className='flex gap-8'>
          <span>{operationStatus[3].icon}</span>
          <div>
            <Typography className='mb-1'>{operationStatus[3].title}</Typography>
            <div
              className={classNames(
                'flex cursor-pointer items-center gap-4 rounded-xl',
                operationStatus[3].bg
              )}
            >
              <Typography variant='caption' weight='bold'>
                {t('on')} {format(new Date(field.updatedAt), 'dd/MM/yyyy')}
              </Typography>
            </div>
          </div>
        </div>
      )}
      {content.filter((item) => item).length < 1 &&
      field.fieldStatus === 'ACTIVE' ? (
        <Typography weight='semiBold' className=' mb-24 mt-8 text-surface-10'>
          {t('No operations to show')}
        </Typography>
      ) : (
        ''
      )}
    </div>
  );
};

export { TooltipContent };
